import { useState } from 'react';
import {
  faxmarkclassicregular,
  facheckclassicregular,
  FontAwesomeIcon,
  IconProp
} from '@grc/ui-package';
import { useI18nState } from '../../../../stores/settings/language-context';
import { useCustomerPortalGlobalStore } from '../../../../stores/customer-portal-global-store';
import {
  applyCustomAccent,
  getCustomAccent,
  normalizeHex
} from '../../../../common/themes/theme-color-utils';

export default function SettingsMenu({ setIsSettingsDrawerOpened }: any) {
  // useCustomerPortalGlobalStore's setter always patches at the store root
  // (Partial<ICustomerPortalGlobalStore>, i.e. { menu, store }), regardless
  // of which nested field the selector reads - see store.tsx's useStore. So
  // updating just `selectedTheme`/`selectedLanguage` means spreading the
  // current `store` slice back in under its own key, not passing the bare
  // field value.
  const [storeState, setGlobalStore] = useCustomerPortalGlobalStore(
    store => store.store
  );
  const { selectedTheme } = storeState;
  const selectedMode = selectedTheme === 'dark' ? 'dark' : 'light';
  const { localeData } = useI18nState();
  const [accentColor, setAccentColor] = useState(getCustomAccent);
  const [hexValue, setHexValue] = useState(getCustomAccent);

  const handleThemeChange = (value: string) => {
    setGlobalStore({ store: { ...storeState, selectedTheme: value } });
    applyCustomAccent(accentColor, value);
  };

  const handleAccentChange = (value: string) => {
    const normalized = normalizeHex(value);
    setHexValue(value);
    if (!normalized) return;
    setAccentColor(normalized);
    setHexValue(normalized);
    applyCustomAccent(normalized, selectedMode);
  };

  const handleLanguageChange = (value: string) => {
    setGlobalStore({ store: { ...storeState, selectedLanguage: value } });
  };

  // Each option maps to its own real, distinct theme value (previously
  // DarkGreen/TurkishAqua both wrote 'green', and Blue/SoothingBreeze both
  // wrote 'blue') - see docs/theme-wiring-plan.md. swatchColor is a fixed
  // preview color for this option itself, not a themed token - it should
  // always show what that theme looks like regardless of the currently
  // active theme.
  const themeOptions = [
    { value: 'light', label: localeData.Light },
    { value: 'dark', label: localeData.Dark }
  ];
  const starterColors = ['#4F46E5', '#2563EB', '#0891B2', '#059669', '#D97706', '#DB2777'];

  return (
    <>
      <div className="themes-language-settings-menu p-3">
        <div className="menu-setting-header d-flex justify-content-between">
          <div className="settings-menu-head">
            <span className="settings-title">
              {localeData.TemplateCustomize}
            </span>
            <span className="settings-sun-title">
              {localeData.Customizeandpreview}
            </span>
          </div>
          <div
            onClick={() => {
              setIsSettingsDrawerOpened((prevState: boolean) => !prevState);
            }}
          >
            <FontAwesomeIcon
              icon={faxmarkclassicregular as IconProp}
              fontSize={20}
              className="cursor-p"
            />
          </div>
        </div>

        <div className="themes-section d-flex flex-column">
          <div className="theme-title">{localeData.ThemeStyle}</div>
          <div className="theme-color-options">
            {themeOptions.map(option => {
              const isSelected = selectedMode === option.value;
              return (
                <div
                  key={option.value}
                  className={`theme-title cursor-p${isSelected ? ' theme-selected' : ''}`}
                  onClick={() => {
                    handleThemeChange(option.value);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                >
                  <div className="theme-color">
                    <span
                      className="theme-swatch"
                      style={{
                        background:
                          option.value === 'dark' ? '#181B21' : '#FFFFFF'
                      }}
                    ></span>
                    {isSelected && (
                      <FontAwesomeIcon
                        className="theme-selected-check"
                        icon={facheckclassicregular as IconProp}
                        fontSize={12}
                      />
                    )}
                  </div>
                  <div className="theme-color-name text-center">
                    {option.label}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="custom-color-section">
            <div className="custom-color-heading">Brand color</div>
            <div className="custom-color-control">
              <label className="custom-color-preview" style={{ background: accentColor }}>
                <input
                  type="color"
                  value={accentColor}
                  aria-label="Choose brand color"
                  onChange={event => handleAccentChange(event.target.value)}
                />
              </label>
              <div className="custom-color-copy">
                <span>Choose any color</span>
                <small>Preview changes instantly</small>
              </div>
              <input
                className="custom-color-hex"
                value={hexValue}
                maxLength={7}
                aria-label="Brand color hex value"
                onChange={event => setHexValue(event.target.value)}
                onBlur={() => handleAccentChange(hexValue)}
                onKeyDown={event => {
                  if (event.key === 'Enter') handleAccentChange(hexValue);
                }}
              />
            </div>
            <div className="starter-colors" aria-label="Suggested brand colors">
              {starterColors.map(color => (
                <button
                  key={color}
                  type="button"
                  className={accentColor === color ? 'selected' : ''}
                  style={{ background: color }}
                  aria-label={`Use ${color}`}
                  onClick={() => handleAccentChange(color)}
                />
              ))}
            </div>
          </div>
        </div>
        <hr></hr>
        <div className="themes-section d-flex flex-column">
          <div className="theme-title">{localeData.Languages}</div>
          <div className="theme-color-options">
            <div
              className="theme-title cursor-p"
              onClick={() => {
                handleLanguageChange('en');
              }}
            >
              <div className="theme-color">
                <img
                  className="cursor-p"
                  src="../assets/svg/united-states.svg"
                  alt=""
                />
              </div>
              <div className="theme-color-name text-center">
                {localeData.UnitedStates}
              </div>
            </div>
            <div
              className="theme-title cursor-p"
              onClick={() => {
                handleLanguageChange('es');
              }}
            >
              <div className="theme-color">
                <img
                  className="cursor-p"
                  src="../assets/svg/spain.svg"
                  alt=""
                />
              </div>
              <div className="theme-color-name text-center">
                {localeData.Spanish}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
